// LoginPage.js
import React, { useState } from 'react';
import { Radio, Button, message } from 'antd';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import styles from './index.module.less';
import logo from '../../images/granules_logo.png';

// Per-region MSAL config pulled from env vars.
// _IN vars fall back to the original vars until the dev server is restarted.
const REGION_CONFIG = {
  IN: {
    clientId:
      process.env.REACT_APP_CLIENT_ID_IN || process.env.REACT_APP_CLIENT_ID,
    authority:
      process.env.REACT_APP_AUTHORITY_IN || process.env.REACT_APP_AUTHORITY
  },
  US: {
    clientId: process.env.REACT_APP_CLIENT_ID_US,
    authority: process.env.REACT_APP_AUTHORITY_US
  }
};

// Module-level cache so we don't recreate instances on every render
const msalInstanceCache = {};

const getMsalInstance = (region) => {
  if (!msalInstanceCache[region]) {
    const { clientId, authority } = REGION_CONFIG[region];

    // Guard: env vars not loaded yet (dev server needs restart)
    // or US credentials haven't been filled in yet
    if (!clientId || !authority) {
      throw new Error(
        `MSAL config for region "${region}" is not set. ` +
          `Please add REACT_APP_CLIENT_ID_${region} and ` +
          `REACT_APP_AUTHORITY_${region} to your .env file and restart the dev server.`
      );
    }

    msalInstanceCache[region] = new PublicClientApplication({
      auth: {
        clientId,
        authority,
        redirectUri: window.location.origin
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
      }
    });
  }
  return msalInstanceCache[region];
};

const MicrosoftLogo = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="0" y="0" width="9.5" height="9.5" fill="#f25022" />
    <rect x="10.5" y="0" width="9.5" height="9.5" fill="#7fba00" />
    <rect x="0" y="10.5" width="9.5" height="9.5" fill="#00a4ef" />
    <rect x="10.5" y="10.5" width="9.5" height="9.5" fill="#ffb900" />
  </svg>
);

/**
 * MSAL v2 leaves an `interaction.status` key in sessionStorage when a popup
 * is closed/cancelled without completing. Any subsequent loginPopup() call
 * throws `interaction_in_progress` until that stale key is removed.
 */
const clearMsalInteractionStatus = () => {
  [sessionStorage, localStorage].forEach((store) => {
    Object.keys(store)
      .filter((key) => key.includes('interaction.status'))
      .forEach((key) => store.removeItem(key));
  });
};

function LoginPage({ loading, onSubmit }) {
  const [region, setRegion] = useState('IN');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // MsalProvider's already-initialized instance — safe to use for IN
  const { instance: providerInstance } = useMsal();

  const handleMicrosoftLogin = async () => {
    // Prevent double-clicks / concurrent popup attempts
    if (isLoggingIn) return;
    setIsLoggingIn(true);

    try {
      /**
       * For IN: reuse the MsalProvider instance (already initialized).
       * For US: create / reuse a separately configured instance.
       */
      const instance =
        region === 'IN' ? providerInstance : getMsalInstance(region);

      // Clear any stale interaction state left by a previous failed attempt
      clearMsalInteractionStatus();

      // For non-provider instances, resolve any pending redirect first
      if (region !== 'IN') {
        await instance.handleRedirectPromise();
      }

      const result = await instance.loginPopup({
        scopes: ['User.Read'],
        prompt: 'select_account'
      });

      onSubmit({ token: result.accessToken, region });
    } catch (err) {
      console.error('Microsoft login error:', err);

      // User deliberately closed the popup — no toast needed
      if (
        err?.errorCode === 'user_cancelled' ||
        err?.errorCode === 'access_denied'
      ) {
        return;
      }

      // Show the real MSAL error message
      message.error(err?.message || 'Sign-in failed. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className={`${styles.fullContainer}`}>
      <div className={styles.leftSide}>
        <div className={styles.loginleft}>
          <h1>Manage your document workflows</h1>
          <p>
            Make your job easy by creating envelopes and sharing it across for
            approvals. Also its very easy to track the shared documents and
            create templates of your own.
          </p>
        </div>
      </div>

      <div className={styles.rightSide}>
        <div className={styles.container}>
          <div className={styles.logoContainer}>
            <img src={logo} alt="logo" />
          </div>
          <h1 className={styles.formtitle} style={{ color: '#0098DA' }}>
            Let&apos;s get started
          </h1>

          <div className={styles.right_outer_container}>
            {/* Region selector */}
            <div className={styles.regionLabel}>
              Select your region <span className={styles.required}>*</span>
            </div>

            <Radio.Group
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className={styles.radioGroup}
            >
              <Radio value="IN" className={styles.radioCard}>
                <span className={styles.flag}>🇮🇳</span>
                <div className={styles.radioCardText}>
                  <span className={styles.radioTitle}>Granules India</span>
                  {/* <span className={styles.regionCode}>IN</span> */}
                </div>
              </Radio>
              <Radio value="US" className={styles.radioCard}>
                <span className={styles.flag}>🇺🇸</span>
                <div className={styles.radioCardText}>
                  <span className={styles.radioTitle}>Granules US</span>
                  {/* <span className={styles.regionCode}>US</span> */}
                </div>
              </Radio>
            </Radio.Group>

            {/* Microsoft Entra sign-in */}
            <Button
              className={styles.msBtn}
              loading={isLoggingIn || loading}
              onClick={handleMicrosoftLogin}
              icon={<MicrosoftLogo />}
            >
              Sign in with Microsoft Entra
            </Button>

            <p className={styles.msHelperText}>
              You will be redirected to your organisation&apos;s Microsoft
              sign-in page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
